import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Tag, Typography, Spin, Empty, Space, Divider } from 'antd';
import { ClockCircleOutlined, SendOutlined, RightOutlined, DownOutlined, WarningOutlined } from '@ant-design/icons';
import { KitchenOrderDto, KdsOrderItemDto, OrderItemWithoutComandaDto } from '../../../types/kitchen';
import {
  useLazyGetKitchenOrdersByOrderIdQuery,
  useLazyGetOrderItemsWithoutComandaQuery,
} from '../../../services/kitchenApi';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';

const { Text } = Typography;

interface ResendOrderModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (
    selectedKitchenOrders: KitchenOrderDto[],
    selectedItemIds: Set<string>,
    /** OrderItemIds de renglones que nunca tuvieron comanda y hay que crearles una */
    selectedMissingItemIds: Set<string>,
  ) => void;
  orderId: string;
}

const statusColors: Record<string, string> = {
  Pending: 'orange',
  Preparing: 'blue',
  Ready: 'green',
  Completed: 'default',
};

const statusLabels: Record<string, string> = {
  Pending: 'Pendiente',
  Preparing: 'Preparando',
  Ready: 'Lista',
  Completed: 'Completada',
};

const getActiveItems = (ko: KitchenOrderDto) => ko.items.filter(i => !i.isCancelled);

const ResendOrderModal: React.FC<ResendOrderModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  orderId,
}) => {
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [selectedMissingIds, setSelectedMissingIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [fetchKitchenOrders, { data: kitchenOrders, isLoading, isFetching }] =
    useLazyGetKitchenOrdersByOrderIdQuery();
  // Este modal leía SOLO de KitchenOrders, así que un producto cuya comanda nunca se creó
  // no aparecía por ningún lado: el mesero veía la lista incompleta, sin señal de que
  // faltaba algo, y no había forma de rescatarlo desde la UI.
  const [fetchMissingItems, { data: missingItems, isFetching: isFetchingMissing }] =
    useLazyGetOrderItemsWithoutComandaQuery();
  const { formatInTimeZone } = useRestaurantTimeZone();

  useEffect(() => {
    if (visible && orderId) {
      fetchKitchenOrders(orderId);
      fetchMissingItems(orderId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, orderId]);

  // Los renglones sin comanda van preseleccionados: nunca llegaron a cocina, así que
  // mandarlos es casi siempre justo lo que se quiere.
  useEffect(() => {
    setSelectedMissingIds(new Set((missingItems || []).map(i => i.orderItemId)));
  }, [missingItems]);

  // Default-select all non-completed, non-cancelled items across ALL KitchenOrders tied to
  // this OrderId. A single comanda can now produce 2 KitchenOrders (daily-menu + regular);
  // we want both cards' pending items selected by default on resend.
  useEffect(() => {
    if (kitchenOrders && kitchenOrders.length > 0) {
      const pendingIds = kitchenOrders.flatMap(ko =>
        ko.items
          .filter(i => !i.isCancelled && !i.isCompleted)
          .map(i => i.id)
      );
      // Fallback: if no pending items anywhere, select the latest KO's active items (old behavior)
      if (pendingIds.length > 0) {
        setSelectedItemIds(new Set(pendingIds));
      } else {
        const target = kitchenOrders[kitchenOrders.length - 1];
        setSelectedItemIds(new Set(getActiveItems(target).map(i => i.id)));
      }
      setExpandedIds(new Set());
    } else {
      setSelectedItemIds(new Set());
      setExpandedIds(new Set());
    }
  }, [kitchenOrders]);

  // Derived state helpers
  const isKoFullySelected = (ko: KitchenOrderDto) => {
    const active = getActiveItems(ko);
    return active.length > 0 && active.every(i => selectedItemIds.has(i.id));
  };

  const isKoPartiallySelected = (ko: KitchenOrderDto) => {
    const active = getActiveItems(ko);
    const count = active.filter(i => selectedItemIds.has(i.id)).length;
    return count > 0 && count < active.length;
  };

  const isKoAnySelected = (ko: KitchenOrderDto) => {
    return getActiveItems(ko).some(i => selectedItemIds.has(i.id));
  };

  // Handlers
  const handleToggleKo = (ko: KitchenOrderDto) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      const activeItems = getActiveItems(ko);
      if (isKoFullySelected(ko)) {
        activeItems.forEach(i => next.delete(i.id));
      } else {
        activeItems.forEach(i => next.add(i.id));
      }
      return next;
    });
  };

  const handleToggleItem = (itemId: string) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleToggleExpand = (koId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(koId)) next.delete(koId);
      else next.add(koId);
      return next;
    });
  };

  const handleToggleMissingItem = (orderItemId: string) => {
    setSelectedMissingIds(prev => {
      const next = new Set(prev);
      if (next.has(orderItemId)) next.delete(orderItemId);
      else next.add(orderItemId);
      return next;
    });
  };

  const missing = missingItems || [];
  const totalActiveItems = kitchenOrders
    ? kitchenOrders.reduce((sum, ko) => sum + getActiveItems(ko).length, 0)
    : 0;
  const totalSelectable = totalActiveItems + missing.length;
  const selectedCount = selectedItemIds.size + selectedMissingIds.size;
  const allSelected = totalSelectable > 0 && selectedCount === totalSelectable;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItemIds(new Set((kitchenOrders || []).flatMap(ko => getActiveItems(ko).map(i => i.id))));
      setSelectedMissingIds(new Set(missing.map(i => i.orderItemId)));
    } else {
      setSelectedItemIds(new Set());
      setSelectedMissingIds(new Set());
    }
  };

  const handleConfirm = () => {
    const selected = (kitchenOrders || []).filter(ko => isKoAnySelected(ko));
    onConfirm(selected, selectedItemIds, selectedMissingIds);
  };

  const renderItem = (item: KdsOrderItemDto, selectable: boolean) => {
    const parts: string[] = [];
    if (item.quantity > 0 && item.productName) {
      parts.push(`${item.quantity}× ${item.productName}`);
    }
    if (item.variants) {
      parts.push(`(${item.variants})`);
    }
    const isSelected = selectedItemIds.has(item.id);
    return (
      <div
        key={item.id}
        style={{
          marginLeft: 16,
          padding: '4px 8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          cursor: selectable ? 'pointer' : 'default',
          borderRadius: 4,
          backgroundColor: isSelected ? 'var(--bg-info-light)' : 'transparent',
        }}
        onClick={(e) => {
          if (selectable) {
            e.stopPropagation();
            handleToggleItem(item.id);
          }
        }}
      >
        {selectable && (
          <Checkbox
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleToggleItem(item.id)}
            style={{ marginTop: 2 }}
          />
        )}
        <div>
          <Text style={{ fontSize: 13 }}>
            {parts.join(' ')}
          </Text>
          {item.notes && (
            <Text
              type="secondary"
              italic
              style={{ fontSize: 12, display: 'block', color: 'var(--color-warning-text)' }}
            >
              Nota: {item.notes}
            </Text>
          )}
        </div>
      </div>
    );
  };

  const renderKitchenOrder = (ko: KitchenOrderDto) => {
    const fullySelected = isKoFullySelected(ko);
    const partiallySelected = isKoPartiallySelected(ko);
    const anySelected = isKoAnySelected(ko);
    const isExpanded = expandedIds.has(ko.id);
    const activeItems = getActiveItems(ko);

    return (
      <div
        key={ko.id}
        style={{
          marginBottom: 8,
          borderRadius: 8,
          border: anySelected ? '2px solid #1890ff' : '1px solid var(--border-color-strong)',
          backgroundColor: anySelected ? 'var(--bg-info-light)' : 'var(--bg-primary)',
          transition: 'all 0.2s',
          overflow: 'hidden',
        }}
      >
        {/* Kitchen order header */}
        <div
          style={{
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => handleToggleExpand(ko.id)}
        >
          <Space>
            <Checkbox
              checked={fullySelected}
              indeterminate={partiallySelected}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleKo(ko);
              }}
            />
            {isExpanded ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
            <ClockCircleOutlined />
            <Text strong>{formatInTimeZone(ko.sentAt, 'HH:mm')}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatInTimeZone(ko.sentAt, 'DD/MM/YYYY')}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({activeItems.length} {activeItems.length === 1 ? 'producto' : 'productos'})
            </Text>
            {ko.isDailyMenuOrder && (
              <Tag color="purple" style={{ margin: 0 }}>Menú del Día</Tag>
            )}
          </Space>
          <Tag color={statusColors[ko.status] || 'default'}>
            {statusLabels[ko.status] || ko.status}
          </Tag>
        </div>

        {/* Expanded items with individual checkboxes */}
        {isExpanded && (
          <div style={{ padding: '0 8px 12px' }}>
            <Divider style={{ margin: '0 0 8px 0' }} />
            {activeItems.map(item => renderItem(item, true))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Panel de renglones sin comanda. Va arriba y con color de advertencia a propósito: es
   * comida pedida que cocina nunca vio, y es lo primero que hay que resolver.
   */
  const renderMissingPanel = () => (
    <div
      style={{
        marginBottom: 12,
        borderRadius: 8,
        border: '2px solid var(--color-warning-border, #ffbb33)',
        backgroundColor: 'var(--bg-warning-light, #fffbe6)',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '12px 16px' }}>
        <Space align="start">
          <WarningOutlined style={{ color: 'var(--color-warning-text)', marginTop: 3 }} />
          <div>
            <Text strong style={{ color: 'var(--color-warning-text)' }}>
              Sin comanda ({missing.length} {missing.length === 1 ? 'producto' : 'productos'})
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              Estos productos están en la cuenta pero nunca llegaron a cocina. Selecciónalos
              para mandarlos ahora.
            </Text>
          </div>
        </Space>
      </div>
      <Divider style={{ margin: '0 0 8px 0' }} />
      <div style={{ padding: '0 8px 12px' }}>
        {missing.map(item => renderMissingItem(item))}
      </div>
    </div>
  );

  const renderMissingItem = (item: OrderItemWithoutComandaDto) => {
    const isSelected = selectedMissingIds.has(item.orderItemId);
    const parts: string[] = [`${item.quantity}× ${item.productName}`];
    if (item.variants) parts.push(`(${item.variants})`);

    return (
      <div
        key={item.orderItemId}
        style={{
          marginLeft: 16,
          padding: '4px 8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          cursor: 'pointer',
          borderRadius: 4,
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleMissingItem(item.orderItemId);
        }}
      >
        <Checkbox
          checked={isSelected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => handleToggleMissingItem(item.orderItemId)}
          style={{ marginTop: 2 }}
        />
        <div>
          <Text style={{ fontSize: 13 }}>{parts.join(' ')}</Text>
          {item.notes && (
            <Text
              type="secondary"
              italic
              style={{ fontSize: 12, display: 'block', color: 'var(--color-warning-text)' }}
            >
              Nota: {item.notes}
            </Text>
          )}
        </div>
      </div>
    );
  };

  const loading = isLoading || isFetching || isFetchingMissing;
  const hasSomethingToShow = (kitchenOrders && kitchenOrders.length > 0) || missing.length > 0;

  return (
    <Modal
      title={
        <Space>
          <SendOutlined />
          <span>Reenviar Comanda</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText={`Reenviar ${selectedCount > 0 ? `(${selectedCount} ${selectedCount === 1 ? 'producto' : 'productos'})` : ''}`}
      cancelText="Cancelar"
      okButtonProps={{ disabled: selectedCount === 0 }}
      width={500}
      styles={{ body: { maxHeight: '60vh', overflow: 'auto' } }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Cargando comandas...</div>
        </div>
      ) : !hasSomethingToShow ? (
        <Empty description="No hay comandas previas" />
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">
              Selecciona las comandas o productos individuales que deseas reenviar:
            </Text>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Checkbox
              checked={allSelected}
              indeterminate={selectedCount > 0 && !allSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              Seleccionar todo ({totalSelectable} productos)
            </Checkbox>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          {missing.length > 0 && renderMissingPanel()}
          {(kitchenOrders || []).map(renderKitchenOrder)}
        </>
      )}
    </Modal>
  );
};

export default ResendOrderModal;
